// Safe markdown renderer (no external dependencies)
// Ported from src/ts/utils.ts - supports headers, bold, italic, strikethrough,
// underline, inline code, links, code blocks, ordered/unordered lists.

export function renderMarkdown(md: string): string {
  if (!md || !md.trim()) return ''

  const escapeHtml = (str: string) =>
    (str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  const safeLink = (url: string) => {
    try {
      const parsed = new URL(url, window.location.origin)
      if (['http:', 'https:', 'mailto:'].includes(parsed.protocol)) return parsed.href
      return ''
    } catch {
      return ''
    }
  }

  const formatInline = (text: string) => {
    let html = escapeHtml(text || '')
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, t, url) => {
      const safe = safeLink(url.trim())
      return safe ? `<a href="${safe}" target="_blank" rel="noreferrer">${t}</a>` : t
    })
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>')
    html = html.replace(/\*(?!\s)([^*]+?)(?<!\s)\*/g, '<em>$1</em>')
    html = html.replace(/_(?!\s)([^_]+?)(?<!\s)_/g, '<em>$1</em>')
    html = html.replace(/~~([^~]+)~~/g, '<del>$1</del>')
    html = html.replace(/\+\+([^+]+)\+\+/g, '<u>$1</u>')
    return html
  }

  const dedentCode = (text: string) => {
    if (!text) return ''
    const lines = text.split('\n')
    while (lines.length && !lines[0].trim()) lines.shift()
    while (lines.length && !lines[lines.length - 1].trim()) lines.pop()
    let minIndent = Infinity
    lines.forEach((ln) => {
      if (!ln.trim()) return
      const match = ln.match(/^([ \t]+)/)
      if (match && match[1]) {
        minIndent = Math.min(minIndent, match[1].length)
      } else {
        minIndent = 0
      }
    })
    if (!Number.isFinite(minIndent) || minIndent === 0) return lines.join('\n')
    return lines
      .map((ln) => (ln.startsWith(' '.repeat(minIndent)) ? ln.slice(minIndent) : ln))
      .join('\n')
  }

  const lines = md.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Array<{ type: string; text?: string; level?: number; content?: string; items?: string[] }> = []
  let i = 0

  const flushParagraph = (acc: string[]) => {
    if (!acc.length) return
    blocks.push({ type: 'para', text: acc.join('\n') })
    acc.length = 0
  }

  while (i < lines.length) {
    const line = lines[i]

    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/)
    if (headerMatch) {
      const level = headerMatch[1].length
      const text = headerMatch[2].trim()
      flushParagraph([])
      blocks.push({ type: 'header', level, text })
      i++
      continue
    }

    if (/^\s*```/.test(line)) {
      const codeLines = []
      i++
      while (i < lines.length && !/^\s*```/.test(lines[i])) {
        codeLines.push(lines[i])
        i++
      }
      if (i < lines.length) i++
      flushParagraph([])
      blocks.push({ type: 'code', content: dedentCode(codeLines.join('\n')) })
      continue
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, '').trim())
        i++
      }
      flushParagraph([])
      blocks.push({ type: 'ul', items })
      continue
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, '').trim())
        i++
      }
      flushParagraph([])
      blocks.push({ type: 'ol', items })
      continue
    }

    if (!line.trim()) {
      flushParagraph([])
      i++
      continue
    }

    const paraLines = [line]
    i++
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^\s*```/.test(lines[i]) &&
      !/^#{1,6}\s+/.test(lines[i])
    ) {
      paraLines.push(lines[i])
      i++
    }
    blocks.push({ type: 'para', text: paraLines.join('\n') })
  }

  return blocks
    .map((block) => {
      if (block.type === 'header') {
        const tag = `h${Math.min(block.level!, 6)}`
        return `<${tag}>${formatInline(block.text!)}</${tag}>`
      }
      if (block.type === 'code') {
        return `<pre><code>${escapeHtml(block.content!)}</code></pre>`
      }
      if (block.type === 'ul') {
        return `<ul>${block.items!.map((item) => `<li>${formatInline(item)}</li>`).join('')}</ul>`
      }
      if (block.type === 'ol') {
        return `<ol>${block.items!.map((item) => `<li>${formatInline(item)}</li>`).join('')}</ol>`
      }
      if (block.type === 'para') {
        return `<p>${formatInline(block.text!).replace(/\n/g, '<br>')}</p>`
      }
      return ''
    })
    .join('')
}
